import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from '@/new/lib/utils';

/**
 * Composable primitives for an in-content settings navigation sidebar.
 *
 * Deliberately standalone — not built on the app-chrome `sidebar.tsx`
 * primitives, which are coupled to `SidebarProvider` (collapse, mobile
 * offcanvas, cookies). This nav just sits beside the content and stays put.
 */

/**
 * Wraps the settings nav and its content area, laying them out side by side and
 * filling the available height. Shared by the matter and organisation settings pages.
 */
const SettingsWrapper = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} data-settings-nav="wrapper" className={cn('flex min-h-0 flex-1', className)} {...props} />
  ),
);
SettingsWrapper.displayName = 'SettingsWrapper';

const SettingsNav = React.forwardRef<HTMLElement, React.ComponentProps<'nav'>>(({ className, ...props }, ref) => (
  <nav
    ref={ref}
    data-settings-nav="root"
    className={cn('flex w-64 shrink-0 flex-col gap-4 border-r p-3', className)}
    {...props}
  />
));
SettingsNav.displayName = 'SettingsNav';

const SettingsNavGroup = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} data-settings-nav="group" className={cn('flex w-full flex-col gap-0.5', className)} {...props} />
  ),
);
SettingsNavGroup.displayName = 'SettingsNavGroup';

const SettingsNavGroupLabel = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-settings-nav="group-label"
      className={cn('px-2 py-1 text-xs font-medium tracking-wide text-gray-400 uppercase', className)}
      {...props}
    />
  ),
);
SettingsNavGroupLabel.displayName = 'SettingsNavGroupLabel';

const settingsNavItemVariants = cva(
  'flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm text-gray-500 outline-none transition-colors hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-teal-500 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-gray-100/60 data-[active=true]:font-medium data-[active=true]:text-gray-900 [&_.iconify]:size-4 [&_.iconify]:shrink-0',
);

interface SettingsNavItemProps extends React.ComponentProps<'button'> {
  /** Render as a child element (e.g. a router `<Link>`) instead of a `<button>`. */
  asChild?: boolean;
  /** Highlights the item as the current section. */
  active?: boolean;
}

/**
 * A single nav entry. Compose the icon and label inside it, e.g.
 * `<SettingsNavItem asChild active={isActive}><Link to={id}><Icon .../>Label</Link></SettingsNavItem>`.
 */
const SettingsNavItem = React.forwardRef<HTMLButtonElement, SettingsNavItemProps>(
  ({ asChild = false, active = false, className, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        ref={ref}
        data-settings-nav="item"
        data-active={active}
        className={cn(settingsNavItemVariants(), className)}
        {...props}
      />
    );
  },
);
SettingsNavItem.displayName = 'SettingsNavItem';

export { SettingsWrapper, SettingsNav, SettingsNavGroup, SettingsNavGroupLabel, SettingsNavItem };
